import React, {useCallback, useEffect, useRef, useState} from "react";
import {Dialog, DialogContent, Grid, Slide, Stack, useMediaQuery} from "@mui/material";
import {
    useSearchResultDetailsState,
    useSearchResultDetailsTranslation,
} from "../search-result-details/SearchResultDetailsContext";
import {ObjectViewCloseButton} from "./ObjectViewCloseButton";
import {ObjectViewMetaSectionWrapper} from "./ObjectViewMetaSectionWrapper";
import {ObjectViewMediaSectionWrapper} from "./ObjectViewMediaSectionWrapper";
import {useObjectViewPreviousNext} from "./useObjectViewPreviousNext";
import {useObjectViewHandlers} from "./useObjectViewHandlers";

const SlideTransition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Component that holds the object view dialog.
 * Adapts its layout to both desktop (≥ 1366 px) and small screens (< 1366 px):
 *   - Desktop: two-column Grid, nav buttons outside the dialog, 6vw margin
 *   - Mobile/tablet: stacked layout with Stack, nav buttons inside the dialog, no margin
 *
 * @param open  bool  Sets the open-state for the dialog.
 */
export const ObjectView = ({open}) => {
    const gridRef = useRef();
    const dialogRef = useRef();
    const t = useSearchResultDetailsTranslation();
    const {model, fullscreen, editMode} = useSearchResultDetailsState();

    // Responsive breakpoints — kept at the hook level (Rules of Hooks)
    const smallScreen = useMediaQuery("(max-width: 1365px)");
    const isPortrait = useMediaQuery("(orientation: portrait)");

    // Number of MUI Grid columns for the media / meta panels
    const [columnDefs, setColumnDefs] = useState([9, 3]);

    // No margin on mobile or when the user has triggered fullscreen mode
    const fullscreenMargin = fullscreen || smallScreen ? 0 : "6vw";

    const {
        openDialogCallback,
        handleOnClose,
        handleMediaPlayClick,
        openDialog,
    } = useObjectViewHandlers({dialogOpen: open});

    const {
        getPreviousButton,
        getNextButton,
        setShowNavigation,
        setPdfOpen,
    } = useObjectViewPreviousNext({
        model,
        smallScreen,
        openDialogCallback,
    });

    const pdfOpenedCallback = (isOpen) => setPdfOpen(isOpen);

    const toggleEditModeOn = useCallback(() => {
        setShowNavigation(false);
        setColumnDefs([7, 5]);
    }, [setShowNavigation]);

    const toggleEditModeOff = useCallback(() => {
        setColumnDefs([9, 3]);
        setShowNavigation(true);
    }, [setShowNavigation]);

    useEffect(() => {
        if (!editMode) {
            toggleEditModeOff();
        } else {
            toggleEditModeOn();
        }
    }, [editMode, toggleEditModeOff, toggleEditModeOn]);

    const mediaWrapper = (
        <ObjectViewMediaSectionWrapper
            t={t}
            gridRef={gridRef}
            columnDefs={columnDefs}
            smallScreen={smallScreen}
            mediaPlayCallback={handleMediaPlayClick}
            pdfOpenedCallback={pdfOpenedCallback}
        />
    );

    const metaWrapper = (
        <ObjectViewMetaSectionWrapper
            t={t}
            smallScreen={smallScreen}
            columnDefs={columnDefs}
            dialogRef={dialogRef}
        />
    );

    return (
        <>
            {/* Desktop only: nav buttons float outside the dialog in the margin area */}
            {!smallScreen && getPreviousButton()}
            {!smallScreen && getNextButton()}

            <Dialog
                open={openDialog}
                onClose={handleOnClose}
                fullScreen={true}
                hideBackdrop={true}
                sx={{margin: fullscreenMargin}}
                slots={{transition: SlideTransition}}
                slotProps={{
                    paper: {
                        sx: {
                            // No border-radius on mobile – dialog fills the full viewport
                            borderRadius: smallScreen ? 0 : 5,
                            backgroundColor: "#bbb",
                        },
                    },
                }}
                transitionDuration={{enter: 450, exit: 200}}
                ref={dialogRef}
            >
                {/*
                  * Mobile: gridRef lives on DialogContent (the outer scrollable container)
                  *         so ResizeObserver in ObjectMediaImage can measure it.
                  * Desktop: gridRef lives on the inner Grid container.
                  */}
                <DialogContent
                    sx={{overflow: "hidden", padding: 0}}
                    ref={smallScreen ? gridRef : undefined}
                >
                    <ObjectViewCloseButton
                        editMode={editMode}
                        closeDialogCallback={handleOnClose}
                    />

                    {smallScreen ? (
                        // Mobile: stack media above meta (portrait) or side-by-side (landscape)
                        <Stack direction={isPortrait ? "column" : "row"}>
                            {getPreviousButton()}
                            {getNextButton()}
                            {mediaWrapper}
                            {metaWrapper}
                        </Stack>
                    ) : (
                        // Desktop: two-column Grid layout
                        <Grid
                            container
                            direction="row"
                            sx={{minWidth: "95%", minHeight: "100%"}}
                            ref={gridRef}
                        >
                            {mediaWrapper}
                            {metaWrapper}
                        </Grid>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
